// Система избранного
export interface FavoriteItem {
  id: string;
  userId: string;
  type: 'medication' | 'vaccine' | 'disease' | 'algorithm' | 'protocol' | 'calculation';
  title: string;
  description: string;
  data: any;
  category: string;
  tags: string[];
  createdAt: Date;
  lastUsed: Date;
  usageCount: number;
}

export interface MedicationFavorite extends FavoriteItem {
  type: 'medication';
  data: {
    name: string;
    category: string;
    dosage: number;
    unit: string;
    frequency: string;
    indications: string[];
    contraindications: string[];
  };
}

export interface VaccineFavorite extends FavoriteItem {
  type: 'vaccine';
  data: {
    name: string;
    age: number;
    schedule: any;
    contraindications: string[];
  };
}

export interface DiseaseFavorite extends FavoriteItem {
  type: 'disease';
  data: {
    name: string;
    category: string;
    symptoms: string[];
    treatment: string;
    images: string[];
  };
}

export interface AlgorithmFavorite extends FavoriteItem {
  type: 'algorithm';
  data: {
    name: string;
    priority: string;
    steps: string[];
    conditions: string[];
  };
}

export interface ProtocolFavorite extends FavoriteItem {
  type: 'protocol';
  data: {
    name: string;
    version: string;
    description: string;
    source: string;
  };
}

export interface CalculationFavorite extends FavoriteItem {
  type: 'calculation';
  data: {
    medication: string;
    age: number;
    weight: number;
    indication: string;
    result: any;
  };
}

// Менеджер избранного
export class FavoritesManager {
  private static instance: FavoritesManager;
  private favorites: FavoriteItem[] = [];

  private constructor() {
    this.loadFavoritesFromStorage();
  }

  public static getInstance(): FavoritesManager {
    if (!FavoritesManager.instance) {
      FavoritesManager.instance = new FavoritesManager();
    }
    return FavoritesManager.instance;
  }

  // Загрузка избранного из localStorage
  private loadFavoritesFromStorage(): void {
    try {
      const stored = localStorage.getItem('pediatrics_favorites');
      if (stored) {
        this.favorites = JSON.parse(stored).map((item: any) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          lastUsed: new Date(item.lastUsed)
        }));
      }
    } catch (error) {
      console.error('Ошибка загрузки избранного:', error);
      this.favorites = [];
    }
  }

  // Сохранение избранного в localStorage
  private saveFavoritesToStorage(): void {
    try {
      localStorage.setItem('pediatrics_favorites', JSON.stringify(this.favorites));
    } catch (error) {
      console.error('Ошибка сохранения избранного:', error);
    }
  }

  // Добавление в избранное
  public addToFavorites(item: Omit<FavoriteItem, 'id' | 'createdAt' | 'lastUsed' | 'usageCount'>): string {
    const newItem: FavoriteItem = {
      ...item,
      id: this.generateId(),
      createdAt: new Date(),
      lastUsed: new Date(),
      usageCount: 0
    };

    this.favorites.push(newItem);
    this.saveFavoritesToStorage();
    return newItem.id;
  }

  // Удаление из избранного
  public removeFromFavorites(itemId: string): boolean {
    const index = this.favorites.findIndex(item => item.id === itemId);
    if (index >= 0) {
      this.favorites.splice(index, 1);
      this.saveFavoritesToStorage();
      return true;
    }
    return false;
  }

  // Проверка, находится ли элемент в избранном
  public isFavorite(userId: string, type: FavoriteItem['type'], data: any): boolean {
    return this.favorites.some(item => 
      item.userId === userId && 
      item.type === type && 
      JSON.stringify(item.data) === JSON.stringify(data)
    );
  }

  // Получение избранного пользователя
  public getUserFavorites(userId: string, type?: FavoriteItem['type']): FavoriteItem[] {
    let userFavorites = this.favorites.filter(item => item.userId === userId);
    
    if (type) {
      userFavorites = userFavorites.filter(item => item.type === type);
    }

    // Сортируем по частоте использования и дате последнего использования
    return userFavorites.sort((a, b) => {
      if (a.usageCount !== b.usageCount) {
        return b.usageCount - a.usageCount;
      }
      return b.lastUsed.getTime() - a.lastUsed.getTime();
    });
  }

  // Обновление счетчика использования
  public markAsUsed(itemId: string): void {
    const item = this.favorites.find(item => item.id === itemId);
    if (item) {
      item.lastUsed = new Date();
      item.usageCount++;
      this.saveFavoritesToStorage();
    }
  }

  // Поиск в избранном
  public searchFavorites(userId: string, query: string): FavoriteItem[] {
    const lowerQuery = query.toLowerCase();
    return this.favorites.filter(item => 
      item.userId === userId && (
        item.title.toLowerCase().includes(lowerQuery) ||
        item.description.toLowerCase().includes(lowerQuery) ||
        item.category.toLowerCase().includes(lowerQuery) ||
        item.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
      )
    );
  }

  // Получение избранного по категориям
  public getFavoritesByCategory(userId: string): Record<string, FavoriteItem[]> {
    const userFavorites = this.favorites.filter(item => item.userId === userId);
    const byCategory: Record<string, FavoriteItem[]> = {};

    userFavorites.forEach(item => {
      if (!byCategory[item.category]) {
        byCategory[item.category] = [];
      }
      byCategory[item.category].push(item);
    });

    return byCategory;
  }

  // Получение популярных элементов
  public getPopularFavorites(userId: string, limit: number = 10): FavoriteItem[] {
    const userFavorites = this.favorites.filter(item => item.userId === userId);
    return userFavorites
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, limit);
  }

  // Получение недавно использованных
  public getRecentFavorites(userId: string, limit: number = 10): FavoriteItem[] {
    const userFavorites = this.favorites.filter(item => item.userId === userId);
    return userFavorites
      .sort((a, b) => b.lastUsed.getTime() - a.lastUsed.getTime())
      .slice(0, limit);
  }

  // Добавление тегов
  public addTags(itemId: string, tags: string[]): boolean {
    const item = this.favorites.filter(fav => fav.userId === this.getCurrentUserId()).find(item => item.id === itemId);
    if (item) {
      const allTags = [...item.tags, ...tags];
      const uniqueTags = Array.from(new Set(allTags));
      const newTags = uniqueTags;
      item.tags = newTags;
      this.saveFavoritesToStorage();
      return true;
    }
    return false;
  }

  // Удаление тегов
  public removeTags(itemId: string, tags: string[]): boolean {
    const item = this.favorites.filter(fav => fav.userId === this.getCurrentUserId()).find(item => item.id === itemId);
    if (item) {
      item.tags = item.tags.filter(tag => !tags.includes(tag));
      this.saveFavoritesToStorage();
      return true;
    }
    return false;
  }

  // Получение всех тегов пользователя
  public getUserTags(userId: string): string[] {
    const userFavorites = this.favorites.filter(item => item.userId === userId);
    const allTags = userFavorites.flatMap(item => item.tags);
    return Array.from(new Set(allTags));
  }

  // Получение статистики избранного
  public getFavoritesStats(userId: string): {
    totalFavorites: number;
    byType: Record<string, number>;
    byCategory: Record<string, number>;
    mostUsed: FavoriteItem | null;
    totalUsage: number;
  } {
    const userFavorites = this.favorites.filter(item => item.userId === userId);
    
    const byType: Record<string, number> = {};
    const byCategory: Record<string, number> = {};
    let totalUsage = 0;
    let mostUsed: FavoriteItem | null = null;

    userFavorites.forEach(item => {
      byType[item.type] = (byType[item.type] || 0) + 1;
      byCategory[item.category] = (byCategory[item.category] || 0) + 1;
      totalUsage += item.usageCount;
      
      if (!mostUsed || item.usageCount > mostUsed.usageCount) {
        mostUsed = item;
      }
    });

    return {
      totalFavorites: userFavorites.length,
      byType,
      byCategory,
      mostUsed,
      totalUsage
    };
  }

  // Экспорт избранного
  public exportFavorites(userId: string): string {
    const userFavorites = this.favorites.filter(item => item.userId === userId);
    return JSON.stringify(userFavorites, null, 2);
  }

  // Очистка избранного пользователя
  public clearUserFavorites(userId: string): void {
    this.favorites = this.favorites.filter(item => item.userId !== userId);
    this.saveFavoritesToStorage();
  }

  // Получение текущего пользователя (заглушка)
  private getCurrentUserId(): string {
    // В реальном приложении это будет получаться из контекста аутентификации
    return 'current_user';
  }

  // Генерация ID
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}

// Экспорт глобального экземпляра
export const favoritesManager = FavoritesManager.getInstance();
